import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserAttribute } from 'amazon-cognito-identity-js';
import { CognitoIdentityProviderClient, SignUpCommand, InitiateAuthCommand, ConfirmSignUpCommand, ResendConfirmationCodeCommand, ListUsersCommand } from '@aws-sdk/client-cognito-identity-provider';
import { useConfig } from '../hooks/useConfig';
import { useAuth } from '../hooks/useAuth';
import { useTranslation } from '../i18n';
import CryptoJS from 'crypto-js';

const CustomAuth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { config, loading: configLoading } = useConfig();
  const { setToken } = useAuth();
  const { t, currentLang } = useTranslation();
  
  console.log('CustomAuth - Current language:', currentLang);
  console.log('CustomAuth - localStorage language:', localStorage.getItem('smartgtd-language'));
  console.log('CustomAuth - Test translation:', t('onboarding.trialBadge'));
  
  const [isSignUp, setIsSignUp] = useState(searchParams.get('mode') === 'signup');
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  
  const [confirmationCode, setConfirmationCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [countryCode, setCountryCode] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const locale = (urlParams.get('lang') || navigator.language).toLowerCase();
    if (locale.startsWith('pt-br') || locale.startsWith('pt_br')) return '+55';
    if (locale.startsWith('pt')) return '+351';
    if (locale.startsWith('es-mx') || locale.startsWith('es_mx')) return '+52';
    if (locale.startsWith('es-ar') || locale.startsWith('es_ar')) return '+54';
    if (locale.startsWith('es')) return '+34';
    if (locale.startsWith('en-gb') || locale.startsWith('en_gb')) return '+44';
    if (locale.startsWith('fr')) return '+33';
    if (locale.startsWith('de')) return '+49';
    if (locale.startsWith('it')) return '+39';
    return '+1';
  });
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [userPool, setUserPool] = useState(null);
  const [, forceUpdate] = useState({});

  // Force re-render when language changes
  useEffect(() => {
    forceUpdate({});
  }, [currentLang]);

  const inputStyle = {
    width: '100%',
    padding: '0.875rem 1rem',
    border: '2px solid #e5e7eb',
    borderRadius: '12px',
    fontSize: '1rem',
    transition: 'all 0.2s',
    outline: 'none',
    fontFamily: 'inherit'
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    color: '#374151',
    fontWeight: '600',
    fontSize: '0.875rem',
    letterSpacing: '0.01em'
  };

  useEffect(() => {
    if (config?.cognito) {
      const poolData = {
        UserPoolId: config.cognito.userPoolId,
        ClientId: config.cognito.clientId
      };
      setUserPool(new CognitoUserPool(poolData));
    }
  }, [config]);

  const handleGoogleSignIn = () => {
    if (!config?.cognito) return;
    
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const callbackUrl = isLocalhost ? 'http://localhost:3000/auth/callback.html' : config.cognito.callbackUrl;
    
    const cognitoUrl = `${config.cognito.baseUrl}/oauth2/authorize?identity_provider=Google&client_id=${config.cognito.clientId}&response_type=code&scope=openid+email+profile&redirect_uri=${encodeURIComponent(callbackUrl)}`;
    
    window.location.href = cognitoUrl;
  };

  const calculateSecretHash = (username, clientId, clientSecret) => {
    return CryptoJS.HmacSHA256(username + clientId, clientSecret).toString(CryptoJS.enc.Base64);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Check if user already exists
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? `https://api.${window.location.hostname.replace('webapp.', '')}/auth/check-user`
        : 'http://localhost:8000/auth/check-user';
      
      const checkResponse = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      const checkData = await checkResponse.json();
      
      if (checkData.exists) {
        setError('An account with this email already exists. Please sign in or use a different email.');
        setLoading(false);
        return;
      }

      const client = new CognitoIdentityProviderClient({ region: config.cognito.region });
      
      const fullPhone = `${countryCode}${phone}`;
      
      const params = {
        ClientId: config.cognito.clientId,
        Username: email,
        Password: password,
        UserAttributes: [
          { Name: 'email', Value: email },
          { Name: 'given_name', Value: firstName },
          { Name: 'family_name', Value: lastName },
          { Name: 'phone_number', Value: fullPhone }
        ]
      };

      if (config?.cognito?.clientSecret) {
        params.SecretHash = calculateSecretHash(email, config.cognito.clientId, config.cognito.clientSecret);
      }

      await client.send(new SignUpCommand(params));
      
      setLoading(false);
      setNeedsConfirmation(true);
    } catch (err) {
      setError(err.message || 'Sign up failed');
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const client = new CognitoIdentityProviderClient({ region: config.cognito.region });
      
      const params = {
        ClientId: config.cognito.clientId,
        Username: email,
        ConfirmationCode: confirmationCode
      };

      if (config?.cognito?.clientSecret) {
        params.SecretHash = calculateSecretHash(email, config.cognito.clientId, config.cognito.clientSecret);
      }

      await client.send(new ConfirmSignUpCommand(params));
      
      setLoading(false);
      setNeedsConfirmation(false);
      setIsSignUp(false);
      alert(t('auth.emailConfirmed'));
    } catch (err) {
      setError(err.message || 'Confirmation failed');
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setLoading(true);

    try {
      const client = new CognitoIdentityProviderClient({ region: config.cognito.region });
      
      const params = {
        ClientId: config.cognito.clientId,
        Username: email
      };

      if (config?.cognito?.clientSecret) {
        params.SecretHash = calculateSecretHash(email, config.cognito.clientId, config.cognito.clientSecret);
      }

      await client.send(new ResendConfirmationCodeCommand(params));
      
      setLoading(false);
      alert(t('auth.codeResent'));
    } catch (err) {
      setError(err.message || 'Failed to resend code');
      setLoading(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const client = new CognitoIdentityProviderClient({ region: config.cognito.region });
      
      const params = {
        AuthFlow: 'USER_PASSWORD_AUTH',
        ClientId: config.cognito.clientId,
        AuthParameters: {
          USERNAME: email,
          PASSWORD: password
        }
      };

      if (config?.cognito?.clientSecret) {
        params.AuthParameters.SECRET_HASH = calculateSecretHash(email, config.cognito.clientId, config.cognito.clientSecret);
      }

      const response = await client.send(new InitiateAuthCommand(params));
      
      // Store tokens in Cognito format (same as Google OAuth)
      const { IdToken, AccessToken, RefreshToken } = response.AuthenticationResult;
      const keyPrefix = `CognitoIdentityServiceProvider.${config.cognito.clientId}`;
      
      // Store user identifier
      localStorage.setItem(`${keyPrefix}.LastAuthUser`, email);
      
      // Store tokens with Cognito format
      localStorage.setItem(`${keyPrefix}.${email}.idToken`, IdToken);
      localStorage.setItem(`${keyPrefix}.${email}.accessToken`, AccessToken);
      if (RefreshToken) {
        localStorage.setItem(`${keyPrefix}.${email}.refreshToken`, RefreshToken);
      }
      
      // Also store in simple format for backward compatibility
      localStorage.setItem('token', IdToken);
      
      // Wait for token to be set in context
      await setToken(IdToken);
      
      setLoading(false);
      
      // Small delay to ensure token is propagated
      setTimeout(() => {
        navigate('/');
      }, 100);
    } catch (err) {
      if (err.name === 'UserNotConfirmedException') {
        setNeedsConfirmation(true);
        setError('Please confirm your email first');
      } else {
        setError(err.message || 'Sign in failed');
      }
      setLoading(false);
    }
  };

  if (configLoading || !userPool) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{ color: 'white', fontSize: '1.2rem' }}>Loading...</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(to bottom right, #eff6ff, #f3e8ff)',
      padding: '2rem 1rem',
      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
    }}>
      <div style={{ maxWidth: '64rem', margin: '0 auto', padding: '3rem 1rem' }}>
        {/* Progress Header */}
        {isSignUp && !needsConfirmation && (
          <div style={{ marginBottom: '3rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <span style={{
                fontSize: '0.875rem',
                color: '#059669',
                fontWeight: '600',
                background: '#d1fae5',
                padding: '0.5rem 1rem',
                borderRadius: '9999px'
              }}>
                {t('onboarding.trialBadge')}
              </span>
            </div>
            
            {/* Wizard Steps */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', maxWidth: '48rem', margin: '0 auto', gap: '0.5rem' }}>
              {/* Step 1 - Plan (Completed) */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{
                  width: '3rem',
                  height: '3rem',
                  borderRadius: '50%',
                  background: '#10b981',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  marginBottom: '0.5rem'
                }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                </div>
                <span style={{ fontSize: '0.75rem', fontWeight: '500' }}>{t('onboarding.stepPlan')}</span>
              </div>
              <div style={{ flex: 1, height: '2px', background: '#10b981', maxWidth: '4rem' }}></div>
              
              {/* Step 2 - Account (Current) */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{
                  width: '3rem',
                  height: '3rem',
                  borderRadius: '50%',
                  background: '#2563eb',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  marginBottom: '0.5rem'
                }}>2</div>
                <span style={{ fontSize: '0.75rem', fontWeight: '500' }}>{t('onboarding.stepAccount')}</span>
              </div>
              <div style={{ flex: 1, height: '2px', background: '#d1d5db', maxWidth: '4rem' }}></div>
              
              {/* Step 3 - Setup */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{
                  width: '3rem',
                  height: '3rem',
                  borderRadius: '50%',
                  background: '#d1d5db',
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  marginBottom: '0.5rem'
                }}>3</div>
                <span style={{ fontSize: '0.75rem', fontWeight: '500', color: '#6b7280' }}>{t('onboarding.stepSetup')}</span>
              </div>
            </div>
          </div>
        )}

        {/* Auth Card */}
        <div style={{
          background: 'white',
          borderRadius: '1.5rem',
          padding: '3rem 2.5rem',
          maxWidth: '480px',
          margin: '0 auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
        }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div style={{
              width: '4rem',
              height: '4rem',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '16px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '1rem',
            boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)'
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <h1 style={{
            fontSize: '2.25rem',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '0.5rem'
          }}>
            SmartGTD
          </h1>
          <p style={{ 
            color: '#666', 
            fontSize: '1rem',
            fontWeight: '500',
            marginBottom: '0.25rem'
          }}>
            {needsConfirmation ? t('auth.confirmEmail') : (isSignUp ? t('auth.createAccount') : t('auth.welcomeBack'))}
          </p>
          {isSignUp && !needsConfirmation && (
            <p style={{ 
              color: '#999', 
              fontSize: '0.875rem',
              lineHeight: '1.5'
            }}>
              AI-powered productivity that transforms how you work
            </p>
          )}
        </div>

        {/* Google Sign In - Top */}
        {!needsConfirmation && (
          <>
            <button
              onClick={handleGoogleSignIn}
              type="button"
              style={{
                width: '100%',
                padding: '1rem',
                background: 'white',
                color: '#374151',
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.75rem',
                transition: 'all 0.2s',
                marginBottom: '1.5rem'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#667eea';
                e.target.style.backgroundColor = '#f9fafb';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.backgroundColor = 'white';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('auth.continueWithGoogle')}
            </button>

            {/* Divider */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              margin: '1.5rem 0',
              color: '#9ca3af',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}>
              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
              <span style={{ padding: '0 1rem' }}>{t('auth.or')}</span>
              <div style={{ flex: 1, height: '1px', background: '#e5e7eb' }}></div>
            </div>
          </>
        )}

        {/* Form */}
        <form onSubmit={needsConfirmation ? handleConfirmSignUp : (isSignUp ? handleSignUp : handleSignIn)}>
          {needsConfirmation ? (
            <>
              <div style={{ marginBottom: '1.25rem', padding: '1rem', background: '#f0f9ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                <p style={{ color: '#1e40af', fontSize: '0.875rem', margin: 0 }}>
                  {t('auth.verificationSent', { email })}
                </p>
              </div>
              <div style={{ marginBottom: '1.25rem' }}>
                <label style={labelStyle}>{t('auth.verificationCode')}</label>
                <input
                  type="text"
                  value={confirmationCode}
                  onChange={(e) => setConfirmationCode(e.target.value)}
                  required
                  placeholder="123456"
                  style={inputStyle}
                />
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={loading}
                  style={{
                    marginTop: '0.5rem',
                    background: 'none',
                    border: 'none',
                    color: '#667eea',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: '600',
                    padding: 0
                  }}
                >
                  {t('auth.resendCode')}
                </button>
              </div>
            </>
          ) : (
            <>
          {isSignUp && (
            <>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>
                {t('auth.firstName')}
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                placeholder="John"
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>
                {t('auth.lastName')}
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                placeholder="Doe"
                style={inputStyle}
                onFocus={(e) => {
                  e.target.style.borderColor = '#667eea';
                  e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
            </>
          )}

          {isSignUp && (
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>
                {t('auth.phone')}
              </label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  style={{
                    ...inputStyle,
                    width: '120px',
                    flexShrink: 0
                  }}
                >
                  <option value="+1">🇺🇸 +1</option>
                  <option value="+55">🇧🇷 +55</option>
                  <option value="+34">🇪🇸 +34</option>
                  <option value="+351">🇵🇹 +351</option>
                  <option value="+44">🇬🇧 +44</option>
                  <option value="+33">🇫🇷 +33</option>
                  <option value="+49">🇩🇪 +49</option>
                  <option value="+39">🇮🇹 +39</option>
                  <option value="+52">🇲🇽 +52</option>
                  <option value="+54">🇦🇷 +54</option>
                </select>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  placeholder="1234567890"
                  style={{...inputStyle, flex: 1}}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#667eea';
                    e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>
          )}

          <div style={{ marginBottom: '1.25rem' }}>
            <label style={labelStyle}>
              {t('auth.email')}
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              style={inputStyle}
              onFocus={(e) => {
                e.target.style.borderColor = '#667eea';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={labelStyle}>
              {t('auth.password')}
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              placeholder="••••••••"
              style={inputStyle}
              onFocus={(e) => {
                e.target.style.borderColor = '#667eea';
                e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e5e7eb';
                e.target.style.boxShadow = 'none';
              }}
            />
            {isSignUp && (
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.5rem' }}>
                Minimum 8 characters
              </p>
            )}
          </div>
          </>
          )}

          {error && (
            <div style={{
              padding: '0.75rem',
              background: '#fee',
              border: '1px solid #fcc',
              borderRadius: '8px',
              color: '#c33',
              fontSize: '0.9rem',
              marginBottom: '1.5rem'
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '1rem',
              background: loading ? '#e5e7eb' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: '700',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow: loading ? 'none' : '0 4px 12px rgba(102, 126, 234, 0.4)',
              letterSpacing: '0.02em'
            }}
            onMouseEnter={(e) => !loading && (e.target.style.transform = 'translateY(-2px)', e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)')}
            onMouseLeave={(e) => (e.target.style.transform = 'translateY(0)', e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)')}
          >
            {loading ? t('auth.processing') : (needsConfirmation ? t('auth.confirmEmail') : (isSignUp ? t('auth.signUp') : t('auth.signIn')))}
          </button>
        </form>

        {!needsConfirmation && (
          <>
        {/* Toggle */}
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <button
            onClick={() => {
              setIsSignUp(!isSignUp);
              setError('');
            }}
            style={{
              background: 'none',
              border: 'none',
              color: '#667eea',
              cursor: 'pointer',
              fontSize: '0.9rem',
              fontWeight: '600'
            }}
          >
            {isSignUp ? t('auth.alreadyHaveAccount') : t('auth.dontHaveAccount')}
          </button>
        </div>
        </>
        )}
      </div>
      </div>
    </div>
  );
};

export default CustomAuth;
